import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./TheBlogsSpherePro.css";
import Navbar from "../Navbar";
import { RAZORPAY_BRAND } from "../../utils/razorpayBranding";

const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const TheBlogsSpherePro = () => {
  const navigate = useNavigate();
  const [hasPro, setHasPro] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  useEffect(() => {
    const checkProStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) { setHasPro(false); setIsExpired(false); return; }
        const response = await fetch(`${BASE_URL}/api/pro-payment/status`, { method: "GET", headers: { Authorization: `Bearer ${token}` } });
        const data = await response.json();
        if (response.ok && data.success && data.expiryDate) {
          const expiryTime = new Date(data.expiryDate).getTime();
          if (data.isPro && expiryTime > Date.now()) { setHasPro(true); setIsExpired(false); setExpiresAt(data.expiryDate); }
          else { setHasPro(false); setIsExpired(true); setExpiresAt(data.expiryDate); }
        } else { setHasPro(false); setIsExpired(false); }
      } catch { setHasPro(false); setIsExpired(false); } finally { setCheckingStatus(false); }
    };
    checkProStatus();
  }, []);

  useEffect(() => {
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) return;
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js"; script.async = true; document.body.appendChild(script);
  }, []);

  const handlePayment = async () => {
    try {
      setLoading(true); setShowConfirm(false);
      const token = localStorage.getItem("token");
      if (!token) { alert("Please login"); setLoading(false); return; }
      if (!window.Razorpay) { alert("Razorpay loading..."); setLoading(false); return; }
      const res = await fetch(`${BASE_URL}/api/pro-payment/create-order`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
      const data = await res.json(); if (!res.ok ||!data.success) throw new Error(data.message);
      const options = {
        key: data.key_id, amount: data.order.amount, currency: data.order.currency,
        ...RAZORPAY_BRAND, description: "TheBlogsSphere Pro", order_id: data.order.id,
        handler: async function (paymentResponse) {
          try {
            const verifyResponse = await fetch(`${BASE_URL}/api/pro-payment/verify`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(paymentResponse) });
            const verifyData = await verifyResponse.json();
            if (verifyResponse.ok && verifyData.success) { setHasPro(true); setIsExpired(false); setExpiresAt(verifyData.expiryDate); alert("🎉 Payment successful!"); navigate("/pro-home", { replace: true }); return; }
            alert(verifyData.message || "Verification failed");
          } catch { alert("Verification failed"); } finally { setLoading(false); }
        },
        theme: { color: "#6c63ff" }, modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options); rzp.on("payment.failed", () => setLoading(false)); rzp.open();
    } catch (e) { alert(e.message); setLoading(false); }
  };

  return (
    <div className="pro-page">
      <Navbar />
      <div className="pro-container">

        <div className="pro-header">
          <div className="pro-icon">💎</div>
          <h1>TheBlogsSphere Pro</h1>
          <p className="pro-tagline">Create. Share. Grow. Earn.</p>
          <p className="pro-description">Upgrade to Pro for more creation power and start earning when readers love your content.</p>
        </div>

        {/* COMPARISON TABLE */}
        <section className="pro-section">
          <h2>📊 Free vs Pro Limits</h2>
          <div className="compare-table-wrap">
            <table className="compare-table">
              <thead><tr><th>Feature</th><th>Free</th><th>Pro 💎</th></tr></thead>
              <tbody>
                <tr><td>Title Length</td><td>100 chars</td><td><strong>250 chars</strong></td></tr>
                <tr><td>Content Length</td><td>500 chars</td><td><strong>2,000 chars</strong></td></tr>
                <tr><td>Images per Blog</td><td>1 Image</td><td><strong>10 Images</strong></td></tr>
                <tr><td>Video Upload</td><td>1 Minute</td><td><strong>1 Hours</strong></td></tr>
                <tr><td>Earnings</td><td>❌ No</td><td><strong>✅ 90% Earnings</strong></td></tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* HOW IT WORKS - CHART */}
        <section className="pro-section">
          <h2>⚙️ How Pro Works - Step by Step</h2>
          <div className="how-grid">
            <div className="how-step"><div className="how-num">1</div><h3>Pay ₹29</h3><p>You pay ₹29 and become a Pro Creator. Your Pro account activated for 30 days.</p></div>
            <div className="how-arrow">→</div>
            <div className="how-step"><div className="how-num">2</div><h3>Upload Pro Blog</h3><p>Go to Write Blog and upload a Pro Blog. It can be travel, story, tips, video, short, anything useful.</p></div>
            <div className="how-arrow">→</div>
            <div className="how-step"><div className="how-num">3</div><h3>Free Preview</h3><p>Your blog will be visible in Free version. Other users will see first 2 blogs + <strong>🔓 Watch Pro - ₹19</strong> button.</p></div>
          </div>
          <div className="how-grid2">
            <div className="how-step big"><div className="how-num">4</div><h3>Reader Clicks Watch Pro</h3><p>When someone likes your intro, they click <strong>Watch Pro - ₹19</strong>. A separate page opens with payment.</p></div>
            <div className="how-arrow">→</div>
            <div className="how-step big highlight"><div className="how-num">5</div><h3>Reader Pays ₹19</h3><p>Reader pays ₹19 to unlock your full Pro blog. <br/> You get <strong>90% = ₹17.1</strong> <br/> Server cost <strong>10% = ₹1.9</strong> goes to platform.</p></div>
          </div>

          {/* EARNING CHART */}
          <div className="earning-chart">
            <h3>💰 Earning Example Chart</h3>
            <div className="earning-box">
              <div className="earn-row"><span>1 User Pays</span><span>₹19</span></div>
              <div className="earn-row green"><span>You Get 90%</span><span>₹17.10</span></div>
              <div className="earn-row gray"><span>Platform 10%</span><span>₹1.90</span></div>
              <div className="earn-divider"></div>
              <div className="earn-row big"><span>100 Users Unlock</span><span>₹1900</span></div>
              <div className="earn-row green big"><span>You Earn</span><span>₹1710</span></div>
              <div className="earn-note">Example: If 100 users watch your blog per month, you get ₹1,710. More views = more earning.</div>
            </div>
          </div>

          <div className="flow-final">
            <div className="flow-dot"></div>
            <p><strong>Final Flow:</strong> Reader likes your free preview → Pays ₹19 → Your full Pro blog (images, 1hr video, long content) becomes visible → You earn instantly.</p>
          </div>
        </section>

        <section className="pro-section">
          <h2>✨ Pro Benefits</h2>
          <div className="benefits-grid">
            <div className="benefit-card"><span>📝</span><h3>250 Title & 2000 Content</h3><p>Write longer, detailed blogs. Free only 100/500.</p></div>
            <div className="benefit-card"><span>🖼️</span><h3>10 Images Upload</h3><p>Free allows 1 image, Pro allows 10 images per blog.</p></div>
            <div className="benefit-card"><span>🎥</span><h3>1 Hours Video</h3><p>Upload up to 1 hours video. Free only 1 minute.</p></div>
            <div className="benefit-card"><span>🔓</span><h3>Paid Readers - ₹19</h3><p>Readers pay ₹19 to unlock your blog. You keep 90%.</p></div>
          </div>
        </section>

        <section className="rules-section">
          <h2>🛡️ Pro Community Rules</h2>
          <div className="rules-list">
            <div className="rule allowed"><span>✓</span><p>Post original, useful and meaningful content.</p></div>
            <div className="rule allowed"><span>✓</span><p>Respect other users and maintain a positive community.</p></div>
            <div className="rule prohibited"><span>✕</span><p>Sexual, adult content is not allowed.</p></div>
            <div className="rule prohibited"><span>✕</span><p>Hate speech, threats, harassment not allowed.</p></div>
          </div>
        </section>

        <section className="payment-section">
          {!checkingStatus && (
            <>
              {hasPro ? (
                <><h2>✅ You are a Pro Member</h2><p>Valid Till: <strong>{formatDate(expiresAt)}</strong></p><div className="price price-disabled"><span style={{ textDecoration: "line-through", opacity: 0.6 }}>₹29</span> <span>/ month</span></div><button className="pay-button pay-button-pro" onClick={() => navigate("/pro-home")}>💎 Go to Pro Dashboard</button></>
              ) : (
                <><h2>{isExpired ? "🔄 Renew Your Pro Membership" : "🚀 Become a Pro Member"}</h2><p>Pay ₹29 and start earning with 90% revenue share.</p><div className="price">₹29 <span>/ month</span></div><button className="pay-button" onClick={() => setShowConfirm(true)} disabled={loading}>{loading? "Opening Payment..." : (isExpired? "🔄 Renew Pro - Pay ₹29" : "Pay ₹29 & Become Pro")}</button><p className="payment-note">🔒 Secure payment by Razorpay.</p></>
              )}
            </>
          )}
          {checkingStatus && <><h2>Checking Membership...</h2><p>Please wait</p></>}
        </section>

        <button className="back-button" onClick={() => navigate(-1)}>← Back to Profile</button>
      </div>

      {showConfirm && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <h3>Confirm Payment</h3>
            <p>Pay <strong>₹29 / month</strong> for TheBlogsSphere Pro?<br/><small>You will get 90% of ₹19 when readers unlock.</small></p>
            <div className="confirm-buttons">
              <button className="btn-cancel" onClick={() => setShowConfirm(false)}>Cancel</button>
              <button className="btn-confirm" onClick={handlePayment} disabled={loading}>{loading? "Opening..." : "Yes, Pay ₹29"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TheBlogsSpherePro;